import { useCallback, useContext, useEffect, useMemo, useRef } from "react";

import { TransformedCustomViewContainer } from "../../components/transformed_custom_view";
import { PivotControl } from "../../constants/pivot_control_options";
import { ChatContext } from "../../contexts/chat/context";
import { LayoutContext } from "../../contexts/layout/context";
import { VersionedDataContext } from "../../contexts/versioned_data/context";
import { useCustomer } from "../../contexts/app/provider";
import {
  forecastCellPrompt,
  isSupportedForecastCellRecipe,
  type CommonCellContextV2,
} from "../../entities/forecast_cell_context";
import { commonRunEditState } from "../../entities/common_run_edit_state";
import {
  renderedViewBindingIdentity,
  type RenderedViewBinding,
} from "../../entities/pivot_view_config";
import { defaultConfig } from "./default_config";
import { paramsToTransformArgs } from "./params_to_transform_args";
import { createForecastTrackingMatrix } from "../../utils/forecast_tracking_matrix";
import {
  createCommonViewContext,
  type CommonViewContextV1,
} from "../../entities/common_view_context";
import type { DatePivotParams } from "../../entities/date_pivot_params";
import { isCommonCurrentViewReceiptId } from "../../contexts/chat/nucleus_sse_client";
import {
  awaitNucleusExplainStart,
  NUCLEUS_EXPLAIN_BUSY_REASON,
  NUCLEUS_EXPLAIN_STALE_REASON,
  rejectNucleusExplainActivation,
} from "../../entities/nucleus_explain_action";
import { useSynchronizedPending } from "../../components/nucleus_explain/use_synchronized_pending";

type PendingForecastCell = Readonly<{
  context: CommonCellContextV2;
  viewContext?: CommonViewContextV1;
  prompt: string;
  runIdentity: string;
  sourceIdentity: string;
  connectionReady: boolean;
}>;

// Keep the affordance fail-closed to the customer matrix implemented by the
// signed Forecast primitives. The backend remains the authorization boundary.
const FORECAST_EXPLAIN_CUSTOMERS = new Set([
  "goodr",
  "oofos",
  "starface_world",
  "cora",
  "cleanboss",
  "goodchop",
  "brooklinen",
  "vincero",
  "peach_and_lily",
]);

const getRunIdentity = (
  run: React.ContextType<typeof VersionedDataContext>["mainRun"],
): string | undefined =>
  run
    ? [
        run.runSettings.organizationId,
        run.runSettings.inputId,
        run.runSettings.workflowId,
        run.runSettings.settingsNameAndVersion,
        run.runSettings.runAttemptId,
      ].join("\u0000")
    : undefined;

export const ForecastTrackingContainer = () => {
  const { selectedCustomerId } = useCustomer();
  const {
    mainRun,
    lastSuccessfulRun,
    comparedRun = null,
  } = useContext(VersionedDataContext);
  const { isComparing, setIsSidebarOpen } = useContext(LayoutContext);
  const {
    connectChat,
    isAwaitingResponse,
    isConnected,
    isConnecting,
    registerCurrentViewData,
    sendMessage,
    transportKind,
  } = useContext(ChatContext);
  const activeRun = mainRun || lastSuccessfulRun;
  const activeRunIdentity = getRunIdentity(activeRun);
  const activeRunIdentityRef = useRef(activeRunIdentity);
  activeRunIdentityRef.current = activeRunIdentity;
  const {
    pending: pendingForecastCell,
    pendingRef: pendingForecastCellRef,
    setPending: setPendingForecastCell,
    clearPending: clearPendingForecastCell,
    replacePending: replacePendingForecastCell,
  } = useSynchronizedPending<PendingForecastCell>();
  const currentRenderedSourceIdentityRef = useRef<string | undefined>(
    undefined,
  );
  const hasRenderedSourceSignalRef = useRef(false);
  const releaseCurrentViewDataRef = useRef<(() => void) | null>(null);

  const supportsForecastCellExplain = Boolean(
    transportKind === "platform" &&
      activeRun &&
      activeRunIdentity &&
      activeRun.status === "RUN_COMPLETED" &&
      activeRun.runSettings.workflowId === "workflow_atomic_pivots" &&
      commonRunEditState(activeRun.runSettings.settingsNameAndVersion) ===
        "none" &&
      !isComparing &&
      comparedRun === null &&
      FORECAST_EXPLAIN_CUSTOMERS.has(activeRun.runSettings.organizationId) &&
      (!selectedCustomerId ||
        selectedCustomerId === activeRun.runSettings.organizationId),
  );
  const supportsForecastCellExplainRef = useRef(supportsForecastCellExplain);
  supportsForecastCellExplainRef.current = supportsForecastCellExplain;
  const supportsForecastViewInspection = Boolean(
    transportKind === "platform" &&
      activeRun &&
      activeRunIdentity &&
      activeRun.status === "RUN_COMPLETED" &&
      activeRun.runSettings.workflowId === "workflow_atomic_pivots" &&
      commonRunEditState(activeRun.runSettings.settingsNameAndVersion) ===
        "none" &&
      !isComparing &&
      comparedRun === null &&
      (!selectedCustomerId ||
        selectedCustomerId === activeRun.runSettings.organizationId),
  );

  const handleRenderedSourceChange = useCallback(
    (source: RenderedViewBinding | undefined, viewParams?: DatePivotParams) => {
      hasRenderedSourceSignalRef.current = true;
      const sourceIdentity = source
        ? renderedViewBindingIdentity(source)
        : undefined;
      currentRenderedSourceIdentityRef.current = sourceIdentity;
      const pending = pendingForecastCellRef.current;
      if (pending && pending.sourceIdentity !== sourceIdentity) {
        clearPendingForecastCell(pending);
      }
      releaseCurrentViewDataRef.current?.();
      releaseCurrentViewDataRef.current = null;
      const viewContext = viewParams
        ? createCommonViewContext({
            surfaceId: "forecast-tracking",
            measure: viewParams.measure,
            dateHierarchy: viewParams.dateHierarchy,
            minDate: viewParams.minDate,
            maxDate: viewParams.maxDate,
            segments: viewParams.segments,
            filters: viewParams.filters,
            postFilters: viewParams.postFilters,
          })
        : undefined;
      if (
        supportsForecastViewInspection &&
        registerCurrentViewData &&
        source &&
        isCommonCurrentViewReceiptId(source.receiptId) &&
        viewContext &&
        getRunIdentity(source.run) === activeRunIdentity
      ) {
        releaseCurrentViewDataRef.current = registerCurrentViewData(
          {
            version: "CommonCurrentViewData.v1",
            surfaceId: "forecast-tracking",
            receiptId: source.receiptId,
          },
          viewContext,
        );
      }
    },
    [
      activeRunIdentity,
      clearPendingForecastCell,
      pendingForecastCellRef,
      registerCurrentViewData,
      supportsForecastViewInspection,
    ],
  );

  useEffect(
    () => () => {
      releaseCurrentViewDataRef.current?.();
      releaseCurrentViewDataRef.current = null;
    },
    [],
  );

  const explainForecastCell = useCallback(
    (
      context: CommonCellContextV2,
      source: RenderedViewBinding,
      viewContext?: CommonViewContextV1,
    ) => {
      const renderedRun = source.run;
      const renderedRunIdentity = getRunIdentity(renderedRun);
      const sourceIdentity = renderedViewBindingIdentity(source);
      if (
        !supportsForecastCellExplain ||
        !isSupportedForecastCellRecipe(context) ||
        !activeRun ||
        !activeRunIdentity ||
        !renderedRunIdentity ||
        renderedRunIdentity !== activeRunIdentity ||
        renderedRun.status !== "RUN_COMPLETED" ||
        renderedRun.runSettings.workflowId !== "workflow_atomic_pivots" ||
        commonRunEditState(renderedRun.runSettings.settingsNameAndVersion) !==
          "none" ||
        !FORECAST_EXPLAIN_CUSTOMERS.has(
          renderedRun.runSettings.organizationId,
        ) ||
        (Boolean(selectedCustomerId) &&
          selectedCustomerId !== renderedRun.runSettings.organizationId) ||
        (hasRenderedSourceSignalRef.current &&
          currentRenderedSourceIdentityRef.current !== sourceIdentity)
      ) {
        return rejectNucleusExplainActivation(NUCLEUS_EXPLAIN_STALE_REASON);
      }
      if (isAwaitingResponse || pendingForecastCellRef.current) {
        return rejectNucleusExplainActivation(NUCLEUS_EXPLAIN_BUSY_REASON);
      }

      const pendingCell = {
        context,
        viewContext,
        prompt: forecastCellPrompt(context),
        runIdentity: renderedRunIdentity,
        sourceIdentity,
        connectionReady: false,
      } as const;
      if (!hasRenderedSourceSignalRef.current) {
        currentRenderedSourceIdentityRef.current = sourceIdentity;
      }
      setPendingForecastCell(pendingCell);
      setIsSidebarOpen(true);
      // A clicked cell keeps the exact-run conversation and refreshes the
      // active surface plus its verified cell and descriptive view context.
      return awaitNucleusExplainStart(
        () => connectChat(renderedRun, "forecast"),
        () => clearPendingForecastCell(pendingCell),
        () => {
          if (
            !supportsForecastCellExplainRef.current ||
            activeRunIdentityRef.current !== pendingCell.runIdentity ||
            currentRenderedSourceIdentityRef.current !==
              pendingCell.sourceIdentity
          ) {
            return false;
          }
          return replacePendingForecastCell(
            pendingCell,
            Object.freeze({ ...pendingCell, connectionReady: true }),
          );
        },
      );
    },
    [
      activeRun,
      activeRunIdentity,
      clearPendingForecastCell,
      connectChat,
      isAwaitingResponse,
      pendingForecastCellRef,
      replacePendingForecastCell,
      selectedCustomerId,
      setPendingForecastCell,
      setIsSidebarOpen,
      supportsForecastCellExplain,
    ],
  );

  useEffect(() => {
    if (!pendingForecastCell) return;
    if (
      !supportsForecastCellExplain ||
      pendingForecastCell.runIdentity !== activeRunIdentity ||
      currentRenderedSourceIdentityRef.current !==
        pendingForecastCell.sourceIdentity
    ) {
      clearPendingForecastCell(pendingForecastCell);
      return;
    }
    if (
      !pendingForecastCell.connectionReady ||
      !isConnected ||
      isConnecting ||
      isAwaitingResponse
    ) {
      return;
    }

    sendMessage(
      pendingForecastCell.prompt,
      pendingForecastCell.context,
      pendingForecastCell.viewContext,
    );
    clearPendingForecastCell(pendingForecastCell);
  }, [
    activeRunIdentity,
    clearPendingForecastCell,
    isAwaitingResponse,
    isConnected,
    isConnecting,
    pendingForecastCell,
    sendMessage,
    supportsForecastCellExplain,
  ]);

  const onForecastCellExplain = useMemo(
    () => (supportsForecastCellExplain ? explainForecastCell : undefined),
    [explainForecastCell, supportsForecastCellExplain],
  );

  return (
    <TransformedCustomViewContainer
      viewControllerConfig={{
        datasetName: "forecast_tracking",
        transformId: "forecast_tracking",
        folder: "outputs",
        title: "Forecast Tracking",
        viewContextSurfaceId: "forecast-tracking",
        dateHierarchyColumnFilter: {
          order_delivery_month: "month",
          // Week only exists on runs made with the engine's
          // forecast_tracking_weekly_enabled setting; the transform falls back to
          // the month axis on runs that predate it.
          order_delivery_week: "week",
        },
        excludedSegmentationsFilter: [],
        measureColumnFilter: {
          forecast: "forecast",
          "realized forecast": "realized forecast",
          orders: "orders",
          delivered: "delivered",
          open: "open",
          remainder: "remainder",
          "pct ordered": "pct ordered",
        },
        excludedFilterColumnFilter: [],
        showControls: [
          PivotControl.DateHierarchy,
          PivotControl.SegmentSelectMulti,
          PivotControl.FilterBy,
          PivotControl.Measure,
          PivotControl.DatePeriodPicker,
          PivotControl.ShowSettings,
          PivotControl.StoreAsDefault,
          PivotControl.Download,
        ],
        paramsToTransformArgs,
        getMatrix: createForecastTrackingMatrix,
        chunkedMatrixTransformId: "forecast_tracking_matrix",
        receiptTransformId: "forecast_tracking",
        excludeDetailRows: true,
        breakdownMeasures: ["delivered"],
        additionalMeasuresWithTotals: ["delivered", "open"],
        onForecastCellExplain,
        cellExplanationEnabled: !isAwaitingResponse && !pendingForecastCell,
        onRenderedSourceChange: handleRenderedSourceChange,
        // Greys out historical (past/current) month columns, matching the
        // demand/supply no-perspective views. Note: forecast_tracking uses a
        // custom matrix renderer (forecast_tracking_matrix.ts) that applies the
        // shading directly via isHistoricalDate; this flag declares intent
        // consistently with the other views.
        shadeHistory: true,
      }}
      defaultConfig={defaultConfig}
    />
  );
};
